'use client';

import { FC, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { ReferenceLocation } from '@/types';
import { useGetReferenceLocations } from '@/queris';
import { COLORS } from '@/constants';
import { useTranslations } from 'next-intl';
import { sortLocations, getOptionLabel, isOptionEqual } from './utils';
import { HighlightMatch } from './HighlightMatch';

type LocationAutocompleteProps = {
  value: ReferenceLocation[];
  onChange: (locations: ReferenceLocation[]) => void;
  placeholder?: string;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  /** Styles for the root Autocomplete wrapper (sizing, layout) */
  sx?: object;
  /** Styles for the inner TextField (borders, colors) */
  inputSx?: object;
};

const MAX_LOCATIONS = 10;

export const LocationAutocomplete: FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'z.B. Wien, 1010',
  label,
  size = 'small',
  fullWidth = true,
  disabled = false,
  sx,
  inputSx,
}) => {
  const t = useTranslations('locationAutocomplete');
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [open, setOpen] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouseDownInDropdown = useRef(false);
  const alertRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    [],
  );

  // Scroll to limit alert when it appears
  useEffect(() => {
    if (showLimitAlert && alertRef.current) {
      alertRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [showLimitAlert]);

  const { data: searchResults = [], isFetching } = useGetReferenceLocations(debouncedInput);

  const sortedResults = [...searchResults].sort((a, b) => {
    if (a.type < b.type) return -1;
    if (a.type > b.type) return 1;
    return sortLocations(a, b);
  });

  const sortedValue = [...value].sort((a, b) => {
    if (a.type < b.type) return -1;
    if (a.type > b.type) return 1;
    return 0;
  });

  const options: ReferenceLocation[] = debouncedInput.length >= 2 ? sortedResults : sortedValue;

  const handleInputChange = (val: string) => {
    setInputValue(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedInput(val), 300);
    setOpen(true);
  };

  const handleToggle = (option: ReferenceLocation) => {
    const isSelected = value.some((v) => isOptionEqual(v, option));
    if (!isSelected && value.length >= MAX_LOCATIONS) {
      setShowLimitAlert(true);
      return;
    }
    setShowLimitAlert(false);
    onChange(isSelected ? value.filter((v) => !isOptionEqual(v, option)) : [...value, option]);
  };

  const getGroupLabel = (type: string): string => {
    if (type === 'ZIP') return t('groupZip');
    if (type === 'MUNICIPALITY') return t('groupMunicipality');
    return t('groupPlace');
  };

  const summaryText = !focused && value.length > 0 ? value.map(getOptionLabel).join(', ') : '';
  const hasContent = focused ? inputValue.length > 0 : value.length > 0;

  return (
    <Autocomplete<ReferenceLocation, true>
      multiple
      value={value}
      inputValue={focused ? inputValue : summaryText}
      open={open && focused}
      onOpen={() => setOpen(true)}
      onClose={(_, reason) => {
        if (reason === 'escape') {
          setOpen(false);
          setFocused(false);
        }
      }}
      onFocus={() => {
        setFocused(true);
        setOpen(true);
      }}
      onBlur={() => {
        if (mouseDownInDropdown.current) return;
        setFocused(false);
        setOpen(false);
        setInputValue('');
        setShowLimitAlert(false);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        setDebouncedInput('');
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input') handleInputChange(newInput);
      }}
      onChange={(event, _, __, details) => {
        // Clicks toggle via renderOption; Backspace must not remove the hidden selection.
        if ((event as React.KeyboardEvent).key === 'Enter' && details) handleToggle(details.option);
      }}
      options={options}
      groupBy={(option) => option.type}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqual}
      filterOptions={(x) => x}
      loading={isFetching}
      loadingText={
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <CircularProgress size={16} />
        </Box>
      }
      disabled={disabled}
      fullWidth={fullWidth}
      forcePopupIcon={false}
      noOptionsText={
        isFetching ? null : <Box sx={{ textAlign: 'center', width: '100%' }}>{t('noResults')}</Box>
      }
      PaperComponent={({ children, ...paperProps }) => (
        <Paper
          {...paperProps}
          onMouseDown={() => {
            mouseDownInDropdown.current = true;
          }}
          onMouseUp={() => {
            mouseDownInDropdown.current = false;
          }}
        >
          {children}
          {showLimitAlert && (
            <Box
              ref={alertRef}
              sx={{
                mx: '8px',
                mb: '8px',
                mt: '6px',
                px: '10px',
                py: '6px',
                borderRadius: '6px',
                bgcolor: COLORS['secondary/50'],
                border: `1px solid ${COLORS['secondary/400']}`,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Typography fontSize={12} color={COLORS['secondary/500']} lineHeight={1.4}>
                {t('limitReached')}
              </Typography>
            </Box>
          )}
        </Paper>
      )}
      componentsProps={{
        popper: {
          placement: 'bottom-start',
          modifiers: [{ name: 'flip', enabled: false }],
          style: { zIndex: 1199 },
        },
      }}
      sx={sx}
      renderTags={() => null}
      renderGroup={(params) => (
        <Box key={params.key} component="li" sx={{ listStyle: 'none' }}>
          <Typography
            fontSize={11}
            fontWeight={600}
            color={COLORS['neutral/500']}
            sx={{
              px: '12px',
              pt: '8px',
              pb: '2px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {getGroupLabel(params.group)}
          </Typography>
          <Box component="ul" sx={{ p: 0, m: 0 }}>
            {params.children}
          </Box>
        </Box>
      )}
      renderOption={(props, option) => {
        const isSelected = value.some((v) => isOptionEqual(v, option));
        const {
          key,
          onClick: _muiOnClick,
          ...liProps
        } = props as React.HTMLAttributes<HTMLLIElement> & {
          key: string;
          onClick?: React.MouseEventHandler<HTMLLIElement>;
        };
        const highlightQuery = debouncedInput || inputValue;

        return (
          <Box
            component="li"
            key={key}
            {...liProps}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle(option);
            }}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: '6px !important',
              px: '12px !important',
              bgcolor: isSelected ? `${COLORS['secondary/50']} !important` : undefined,
              '&:hover': {
                bgcolor: isSelected
                  ? `${COLORS['secondary/100']} !important`
                  : `${COLORS['neutral/100']} !important`,
              },
            }}
          >
            <Checkbox
              checked={isSelected}
              size="small"
              disableRipple
              sx={{
                p: 0,
                flexShrink: 0,
                color: COLORS['neutral/400'],
                '&.Mui-checked': { color: COLORS['secondary/500'] },
              }}
            />
            <Box>
              <Typography
                fontSize={14}
                fontWeight={option.type === 'ZIP' ? 600 : 400}
                color={isSelected ? COLORS['secondary/700'] : 'inherit'}
              >
                <HighlightMatch text={option.name} query={highlightQuery} />
              </Typography>
              {option.type === 'PLACE' && option.postcode ? (
                <Typography fontSize={12} color={COLORS['neutral/500']} lineHeight={1.3}>
                  {option.postcode}
                </Typography>
              ) : option.type !== 'ZIP' && option.foundBy ? (
                <Typography fontSize={12} color={COLORS['neutral/500']} lineHeight={1.3}>
                  <HighlightMatch text={option.foundBy} query={highlightQuery} />
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <Box sx={{ position: 'relative' }}>
          <TextField
            {...params}
            label={label}
            placeholder={!focused && value.length > 0 ? '' : placeholder}
            size={size}
            sx={{
              ...inputSx,
              '& .MuiInputLabel-shrink:not(.Mui-focused)': { color: 'rgba(0, 0, 0, 0.6)' },
              '& .MuiInputBase-input': { pr: value.length > 0 ? '40px' : undefined },
            }}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink:
                focused ||
                hasContent ||
                (params.InputLabelProps as { shrink?: boolean } | undefined)?.shrink,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: null,
            }}
          />
          {(isFetching || (focused && value.length > 0)) && (
            <Box
              sx={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isFetching ? (
                <CircularProgress size={14} />
              ) : (
                <Typography fontSize={11} color={COLORS['neutral/400']}>
                  {value.length}/{MAX_LOCATIONS}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    />
  );
};
